import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { DashboardLayoutPage } from './layouts/dashboard/DashboardLayoutPage';
import { PageNotFound } from './pages/splash/PageNotFound';
import { UsersPage } from './pages/users/UsersPage';
import { LoginPage } from './pages/login/LoginPage';
import { LogoutPage } from './pages/login/LogoutPage';
import { ForbiddenPage } from './pages/splash/ForbiddenPage';
import { PilotDetailsPage } from './pages/users/PilotDetailsPage';
import { BalancingStatesPage } from './pages/users/BalancingStatesPage';
import AssetsPage from './pages/assets/Assets'
import AssetLanding from './pages/assets/AssetLanding'
import AssetLookup from './pages/assets/AssetLookup'
import AssetAdd from './pages/assets/AssetAdd'
import PortfolioEventPage from './pages/users/PortfolioEventPage'
import HomePage from './pages/home/HomePage'
import {UserPage} from "./pages/users/UserPage";
import PortfolioEventsPage from "./pages/portfolioEvents/PortfolioEventsPage";
import {InstitutionPage} from "./pages/users/InstitutionPage";
import TracePage from "./pages/traceVisualizer/TracePage";
import PortfoliosPage from "./pages/users/UserPortfolioPage";
import {PilotsPage} from "./pages/pilots/PilotsPage";
import {PortfolioPositionsPage} from "./pages/PortfolioPositionsPage";
import { WorkflowsPage } from './pages/developer/workflows/WorkflowsPage';
import { WorkflowDetailPage } from './pages/developer/workflows/WorkflowDetailPage';
import { WorkflowExecutionView } from './pages/developer/workflows/WorkflowExecutionView';
import { DeveloperLayout } from './layouts/developer/DeveloperLayout';
import {createTheme, ThemeProvider} from "@mui/material/styles";
import YodaPage from "./pages/users/YodaPage";
import {DarkModeProvider, useDarkMode} from "./contexts/DarkModeContext";
import { NotificationsPage } from './pages/developer/notifications/NotificationsPage';
import { NotificationCampaignsPage } from './pages/developer/notifications/NotificationCampaignsPage';
import { NotificationCampaignDetailsPage } from './pages/developer/notifications/NotificationCampaignDetailsPage';
import { CustomNotificationCampaignsPage } from './pages/developer/notifications/CustomNotificationCampaignsPage';
import {AllFeatureFlagsPage} from "./pages/developer/release/featureFlags/AllFeatureFlagsPage";
import {ReleaseAppsPage} from "./pages/developer/release/ReleaseAppsPage";
import {ReleaseDetailPage} from "./pages/developer/release/ReleaseDetailPage";
import {TradingMetricsPage} from "./pages/trading-metrics/TradingMetricsPage";
import {BalancingStatesMetricsPage} from "./pages/balancing-states-metrics/BalancingStatesMetricsPage";
import {DrilldownBalancingStatesPage} from "./pages/balancing-states-metrics/DrilldownBalancingStatesPage";
import { CreateCampaignsPage } from './pages/developer/notifications/CreateCampaignsPage';
import { MetricsAppsPage } from './pages/developer/metrics/MetricsAppsPage';
import { RestrictionsPage } from './pages/developer/restrictions/RestrictionsPage';
import { RestrictionDetailPage } from './pages/developer/restrictions/RestrictionDetailPage';
import { LPOAPage } from './pages/developer/lpoa/LPOAPage';
import { GiftsPage } from './pages/developer/gifts/GiftsPage';
import { TestGroupsPage } from './pages/developer/test-groups/TestGroupsPage';
import { TestGroupDetailPage } from './pages/developer/test-groups/TestGroupDetailPage';
import { MeteredUsagePage } from './pages/users/MeteredUsagePage';

const getTheme = (darkMode) =>
    createTheme({
        palette: {
            mode: darkMode ? 'dark' : 'light',
        },
    });

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route path='/forbidden' element={<ForbiddenPage />} />
      <Route path='/login' element={<LoginPage />} />
      <Route path='/logout' element={<LogoutPage />} />
      <Route element={<DashboardLayoutPage />}>
        <Route path='/' element={<HomePage />} />
        <Route path='/users' element={<UsersPage />} />
        <Route path='/users/:userKey' element={<UserPage />} />
        <Route path='/assets' element={<AssetsPage />} />
        <Route path='/assets/lookup' element={<AssetLookup />} />
        <Route path='/assets/add' element={<AssetAdd />} />
        <Route path='/assets/:symbol' element={<AssetLanding />} />
        <Route path='/pilot-details/:autoPilotSettingsKey' element={<PilotDetailsPage />} />
        <Route path='/portfolios/:portfolioKey' element={<PortfoliosPage />} />
        <Route path='/portfolios/:portfolioKey/positions/:userKey?' element={<PortfolioPositionsPage />} />
        <Route path='/*' element={<PageNotFound />} />
        <Route path='/yoda/:portfolioKey' element={<YodaPage />} />
        <Route path='/portfolio-event/:eventKey' element={<PortfolioEventPage />} />
        <Route path='/institution/:institutionKey' element={<InstitutionPage />} />
        <Route path='/portfolio-events' element={<PortfolioEventsPage />} />
        <Route path='/trace/:traceID' element={<TracePage />} />
        <Route path='/pilots' element={<PilotsPage/>} />
        <Route path='/trading-metrics' element={<TradingMetricsPage/>} />
        <Route path='/balancing-states-metrics' element={<BalancingStatesMetricsPage/>} />
        <Route path='/balancing-states-metrics/drilldown' element={<DrilldownBalancingStatesPage/>} />
        <Route path='/users/balancing-states/:autoPilotSettingsKey' element={<BalancingStatesPage />} />
        <Route path='/users/metered-usage/:userKey' element={<MeteredUsagePage />} />

        {/* Developer Routes */}
        <Route path='/developer' element={<DeveloperLayout />}>
          <Route path='workflows' element={<WorkflowsPage />} />
          <Route path='workflows/:workflowId' element={<WorkflowDetailPage />} />
          <Route path='workflows/:workflowId/crons' element={<WorkflowDetailPage />} />
          <Route path='workflows/:workflowName/executions/:executionId' element={<WorkflowExecutionView />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="notifications/templates/:templateKey/:templateType/campaigns" element={<NotificationCampaignsPage />} />
          <Route path="notifications/campaigns/:campaignKey" element={<NotificationCampaignDetailsPage />} />
          <Route path="notifications/custom-campaigns" element={<CustomNotificationCampaignsPage />} />
          <Route path="notifications/custom-campaigns/create" element={<CreateCampaignsPage />} />
          <Route path="release" element={<ReleaseAppsPage />} />
          <Route path="release/:releaseKey" element={<ReleaseDetailPage />} />
          <Route path="release/feature-flags" element={<AllFeatureFlagsPage />} />
          <Route path="metrics/*" element={<MetricsAppsPage />} />
          <Route path="restrictions" element={<RestrictionsPage />} />
          <Route path="restrictions/:restrictionKey" element={<RestrictionDetailPage />} />
          <Route path="lpoa" element={<LPOAPage />} />
          <Route path="gifts" element={<GiftsPage />} />
          <Route path="test-groups" element={<TestGroupsPage />} />
          <Route path="test-groups/:parentGroupId" element={<TestGroupDetailPage />} />
        </Route>
      </Route>
    </Route>
  )
);

function AppRouterContent() {
    const { darkMode } = useDarkMode();
    const theme = getTheme(darkMode);
    return <ThemeProvider theme={theme}><RouterProvider router={router} /></ThemeProvider>;
}

export function AppRouter() {
    return (
        <DarkModeProvider>
            <AppRouterContent />
        </DarkModeProvider>
    );
}
